import * as React from 'react';
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  BeautifulMentionsItem,
  BeautifulMentionsMenuItemProps,
  BeautifulMentionsPlugin,
  BeautifulMentionComponentProps,
  BeautifulMentionsMenuProps,
  BeautifulMentionNode,
} from 'lexical-beautiful-mentions';
import { $applyNodeReplacement, type SerializedLexicalNode } from 'lexical';
import { fetchMentionSuggestions, MentionCandidate } from '@/hooks/apiHooks';
import { Box, Stack, StackProps, styled } from '@mui/material';
import Skeleton from '@/components/@IntranetLibrary/Skeleton';
import { Caption } from '@/components/@IntranetLibrary/Typography';
import { debounce } from 'lodash-es';
import {
  appendMentionPage,
  createMentionPage,
  hasNextMentionPage,
  MentionPage,
} from './mentionPage';

export type MentionItemProps = BeautifulMentionsItem & {
  id: string;
  name: string;
  profileImageUrl: string;
  positionName: string;
  companyName: string;
  departmentName: string;
  divisionName: string;
  value: string;
};
const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;
/** 불러온 목록을 이 비율만큼 내려보면 다음 페이지를 미리 불러, 바닥에 닿기 전에 이어 붙인다. */
const LOAD_MORE_SCROLL_RATIO = 0.5;
/** 첫 검색은 박스 높이(후보 5명)만큼, 다음 페이지는 목록 끝에 몇 줄만 자리표시로 보여 준다. */
const SEARCH_SKELETON_COUNT = 5;
const LOAD_MORE_SKELETON_COUNT = 3;

type MentionSearch = (
  trigger: string,
  queryString?: string | null,
) => Promise<MentionItemProps[]>;

/** requestId(queryIdRef) 는 이 페이지를 연 검색 요청 — 검색어가 바뀐 뒤 도착한 응답을 가려내는 기준. */
type LoadedMentionPage = { requestId: number; page: MentionPage };

type MentionMenuState = {
  isTyping: boolean;
  isEmpty: boolean;
  isLoadingMore: boolean;
  onReachEnd: () => void;
};

// 메뉴 컴포넌트를 렌더마다 새로 만들면 다시 마운트되면서 스크롤 위치가 맨 위로 돌아간다.
// 컴포넌트는 고정해 두고 상태는 context 로 내려준다.
const MentionMenuContext = createContext<MentionMenuState>({
  isTyping: false,
  isEmpty: false,
  isLoadingMore: false,
  onReachEnd: () => undefined,
});

const toMentionItem = (item: MentionCandidate): MentionItemProps => ({
  display: item.name,
  value: item.name,
  id: item.passportId,
  name: item.name,
  profileImageUrl: item.profileImageUrl,
  positionName: item.positionName,
  companyName: item.companyName,
  departmentName: item.departmentName,
  divisionName: item.divisionName,
});

const MentionPlugin = () => {
  const queryIdRef = useRef(0);
  const currentRequestControllerRef = useRef<AbortController | null>(null);
  const loadMoreControllerRef = useRef<AbortController | null>(null);
  const loadedPageRef = useRef<LoadedMentionPage | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const cancelPendingRequest = () => {
    debouncedSearchRef.current.cancel();
    if (currentRequestControllerRef.current) {
      currentRequestControllerRef.current.abort();
      currentRequestControllerRef.current = null;
    }
    if (loadMoreControllerRef.current) {
      loadMoreControllerRef.current.abort();
      loadMoreControllerRef.current = null;
      setIsLoadingMore(false);
    }
  };
  const debouncedSearchRef = useRef(
    debounce(
      async (
        requestId: number,
        query: string,
        resolve: (items: MentionItemProps[]) => void,
      ) => {
        cancelPendingRequest();

        if (query.length < MIN_QUERY_LENGTH) {
          setIsEmpty(true);
          setIsTyping(false);
          resolve([]);
          return;
        }

        const controller = new AbortController();
        currentRequestControllerRef.current = controller;
        const signal = controller.signal;

        try {
          const { data, meta } = await fetchMentionSuggestions(query, {
            signal,
          });

          if (requestId !== queryIdRef.current) {
            resolve([]);
            return;
          }

          const page = createMentionPage(query, data, meta);
          loadedPageRef.current = { requestId, page };
          resolve(page.candidates.map(toMentionItem));
        } catch {
          resolve([]);
        } finally {
          setIsTyping(false);
          if (currentRequestControllerRef.current === controller) {
            currentRequestControllerRef.current = null;
          }
        }
      },
      SEARCH_DEBOUNCE_MS,
    ),
  );

  useEffect(() => {
    return () => {
      cancelPendingRequest();
    };
  }, []);

  const handleSearch = useCallback(
    (
      trigger: string,
      queryString?: string | null,
    ): Promise<MentionItemProps[]> => {
      const query = (queryString ?? '').trim();
      // 같은 검색 요청의 같은 검색어로 다시 불리면(다음 페이지를 붙인 뒤 onSearch 교체 등) 몇 번이든
      // 쌓아 둔 목록을 그대로 돌려준다. 새 검색으로 처리하면 1페이지로 돌아가 스크롤이 맨 위로 튄다.
      const loaded = loadedPageRef.current;
      if (
        loaded?.requestId === queryIdRef.current &&
        loaded.page.query === query
      ) {
        return Promise.resolve(loaded.page.candidates.map(toMentionItem));
      }

      loadedPageRef.current = null;
      setIsTyping(true);
      cancelPendingRequest();

      if (query.length < MIN_QUERY_LENGTH) {
        queryIdRef.current += 1;
        setIsEmpty(true);
        setIsTyping(false);
        return Promise.resolve([]);
      }

      setIsEmpty(false);
      const requestId = ++queryIdRef.current;
      return new Promise<MentionItemProps[]>((resolve) => {
        debouncedSearchRef.current(requestId, query, resolve);
      });
    },
    [],
  );

  // 라이브러리는 onSearch 가 바뀔 때만 결과를 다시 받아 간다. 다음 페이지를 붙인 뒤
  // onSearch 를 새 함수로 바꿔 같은 검색어로 다시 부르게 하고, 그때 쌓아 둔 목록을 돌려준다.
  const [onSearch, setOnSearch] = useState<MentionSearch>(() => handleSearch);

  const loadNextPage = useCallback(async () => {
    const loaded = loadedPageRef.current;
    if (
      !loaded ||
      loaded.requestId !== queryIdRef.current ||
      !hasNextMentionPage(loaded.page) ||
      loadMoreControllerRef.current
    ) {
      return;
    }

    const controller = new AbortController();
    loadMoreControllerRef.current = controller;
    setIsLoadingMore(true);
    try {
      const { data, meta } = await fetchMentionSuggestions(loaded.page.query, {
        signal: controller.signal,
        currentPage: loaded.page.currentPage + 1,
      });
      // 그 사이 새 검색이 시작됐으면(loadedPageRef 를 비우거나 바꿈) 이전 검색어 응답이므로 버린다.
      if (loadedPageRef.current !== loaded) return;

      loadedPageRef.current = {
        requestId: loaded.requestId,
        page: appendMentionPage(loaded.page, data, meta),
      };
      setOnSearch(
        () => (trigger: string, queryString?: string | null) =>
          handleSearch(trigger, queryString),
      );
    } catch (err) {
      // 검색어가 바뀌어 취소한 요청은 무시한다. 그 밖의 실패는 다음 스크롤에서 다시 시도한다.
      if (!controller.signal.aborted) console.error(err);
    } finally {
      if (loadMoreControllerRef.current === controller) {
        loadMoreControllerRef.current = null;
        setIsLoadingMore(false);
      }
    }
  }, [handleSearch]);

  const menuState = useMemo<MentionMenuState>(
    () => ({ isTyping, isEmpty, isLoadingMore, onReachEnd: loadNextPage }),
    [isTyping, isEmpty, isLoadingMore, loadNextPage],
  );

  return (
    <MentionMenuContext.Provider value={menuState}>
      <BeautifulMentionsPlugin
        triggers={['@']}
        searchDelay={0}
        menuItemLimit={false}
        // 기본값(true)이면 포커스가 에디터를 떠날 때(등록 버튼·다른 입력창 클릭 포함) 하이라이트된
        // 후보가 멘션으로 들어간다. 고르지 않은 사람이 멘션·알림되지 않게 끈다.
        insertOnBlur={false}
        onSearch={onSearch}
        menuComponent={CustomMenu}
        menuItemComponent={CustomMenuItem}
        emptyComponent={CustomMenu}
      />
    </MentionMenuContext.Provider>
  );
};

export default MentionPlugin;

const MentionItemSkeletons = ({ count }: { count: number }) => (
  <>
    {Array.from({ length: count }, (_, i) => (
      <Stack
        key={i}
        direction="row"
        alignItems="center"
        gap="8px"
        padding="4px"
      >
        <Skeleton variant="circle" height="28px" />
        <Skeleton variant="rect" width="60%" height="14px" />
      </Stack>
    ))}
  </>
);

function CustomMenu({ loading, children }: BeautifulMentionsMenuProps) {
  const { isTyping, isEmpty, isLoadingMore, onReachEnd } =
    useContext(MentionMenuContext);
  const hasItems = React.Children.count(children) > 0;

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight * LOAD_MORE_SCROLL_RATIO) {
      onReachEnd();
    }
  };

  return (
    <>
      {!isEmpty && (
        <Wrapper onScroll={handleScroll}>
          {/* 다음 페이지를 붙이는 동안에도 loading 이 잠깐 켜지므로, 목록이 있으면 그대로 둔다 */}
          {isTyping || (loading && !hasItems) ? (
            <MentionItemSkeletons count={SEARCH_SKELETON_COUNT} />
          ) : hasItems ? (
            <>
              {children}
              {isLoadingMore && (
                <MentionItemSkeletons count={LOAD_MORE_SKELETON_COUNT} />
              )}
            </>
          ) : (
            <Caption>
              일치하는 결과가 없어요.
              <br />
              검색어를 다시 한 번 확인해 주세요.
            </Caption>
          )}
        </Wrapper>
      )}
    </>
  );
}

const CustomMenuItem = forwardRef<
  HTMLDivElement,
  BeautifulMentionsMenuItemProps
>(
  (
    {
      item,
      selected,
      id,
      name,
      profileImageUrl,
      positionName,
      companyName,
      departmentName,
      ...props
    },
    ref,
  ) => {
    const mention = item.data as MentionItemProps;
    if (!mention) return null;

    return (
      <MentionItem data-id={id} selected={selected} {...props} ref={ref}>
        <img src={profileImageUrl} alt={`${name}의 프로필 사진`} />
        <span className="mention-name">
          {name} {positionName}
        </span>
        <span className="mention-department">
          {companyName} {departmentName}
        </span>
      </MentionItem>
    );
  },
);
CustomMenuItem.displayName = 'CustomMenuItem';

const MentionItem = styled(
  ({ children, ...props }: StackProps & { selected: boolean }) => (
    <Stack {...props}>{children}</Stack>
  ),
)<{ selected: boolean }>(({ selected }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  padding: '4px',
  gap: '8px',
  alignItems: 'center',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: '400',
  overflow: 'hidden',
  lineHeight: '20px',

  backgroundColor: selected ? '#E7E9EC' : '#fff',

  '& img': {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    flex: 'none',
    border: '1px solid rgba(0, 0, 0, 0.10)',
  },
  '& .mention-name': {
    color: 'var(--dk-text, #000)',
    flex: 'none',
  },
  '& .mention-department': {
    display: 'block',
    color: 'var(--dk-text-secondary, #888D96)',
    paddingLeft: '8px',
    borderLeft: '1px solid var(--dk-line, #e7e9ec)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  ':hover': { backgroundColor: 'var(--dk-bg-strong, #E7E9EC)' },
}));

const Wrapper = styled(({ children, ...props }: StackProps) => (
  <Stack {...props}>{children}</Stack>
))(() => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '294px',
  padding: '16px 12px',
  boxSizing: 'border-box',
  background: 'var(--dk-bg, #fff)',
  border: '1px solid var(--dk-line, #e7e9ec)',
  borderRadius: '4px',
  boxShadow: '0px 6px 12px rgba(0,0,0,0.08)',
  // 후보 5명까지 보이는 높이, 나머지는 스크롤
  maxHeight: '246px',
  overflowY: 'auto',
  '& > *': { flexShrink: 0 },
}));

export const CustomMentionComponent = forwardRef<
  HTMLSpanElement,
  BeautifulMentionComponentProps
>(({ trigger, data }, ref) => {
  const mention = data as MentionItemProps;
  if (!mention) return null;

  return (
    <Box
      component="span"
      ref={ref}
      className="mention-name"
      data-id={mention.id}
      sx={{
        display: 'inline-block',
        cursor: 'pointer',
        padding: '2px 4px',
        borderRadius: '4px',
        background: 'var(--dk-bg-muted, #f3f5f6)',
        color: 'var(--dk-accent, #0066cc)',
        fontSize: '14px',
        lineHeight: '20px',
      }}
    >
      {trigger}
      {mention.name}
      {mention.departmentName && `(${mention.departmentName})`}
    </Box>
  );
});
CustomMentionComponent.displayName = 'CustomMentionComponent';

// V1(레거시 TinyMCE) 뷰어 호환 멘션 노드 — 기본 노드 exportDOM 은 V1 뷰어가 칩으로 못 읽는다.
// 저장 HTML 을 V1 포맷(span.mention-name[data-id] + "@이름(팀명)")으로 내보내되,
// data-lexical-* 도 함께 남겨 재편집(importDOM) 호환 유지.
export class V1CompatMentionNode extends BeautifulMentionNode {
  static getType(): string {
    return 'v1CompatMention';
  }

  static clone(node: V1CompatMentionNode): V1CompatMentionNode {
    return new V1CompatMentionNode(
      node.getTrigger(),
      node.getValue(),
      node.getData(),
      node.getKey(),
    );
  }

  static importJSON(
    serializedNode: SerializedLexicalNode,
  ): V1CompatMentionNode {
    const n = serializedNode as unknown as {
      trigger: string;
      value: string;
      data?: Record<string, unknown>;
    };
    return $createV1CompatMentionNode(n.trigger, n.value, n.data);
  }

  exportJSON() {
    return { ...super.exportJSON(), type: 'v1CompatMention' as const };
  }

  component() {
    return CustomMentionComponent;
  }

  exportDOM(): { element: HTMLElement } {
    const trigger = this.getTrigger();
    const value = this.getValue();
    const data = this.getData() as unknown as MentionItemProps | undefined;

    const element = document.createElement('span');
    // V1 호환: 레거시 .mention-name CSS/식별자/팀명 포함 텍스트
    element.classList.add('mention-name');
    element.setAttribute('contenteditable', 'false');
    if (data?.id) element.setAttribute('data-id', String(data.id));
    // V2 재편집 호환: 라이브러리 importDOM 이 찾는 속성 유지 (V1 은 무시)
    element.setAttribute('data-lexical-beautiful-mention', 'true');
    element.setAttribute('data-lexical-beautiful-mention-trigger', trigger);
    element.setAttribute('data-lexical-beautiful-mention-value', value);
    if (data) {
      element.setAttribute(
        'data-lexical-beautiful-mention-data',
        JSON.stringify(data),
      );
    }
    const team = data?.departmentName;
    element.textContent = `${trigger}${value}${team ? `(${team})` : ''}`;
    return { element };
  }
}

export function $createV1CompatMentionNode(
  trigger: string,
  value: string,
  data?: Record<string, unknown>,
): V1CompatMentionNode {
  return $applyNodeReplacement(
    new V1CompatMentionNode(
      trigger,
      value,
      data as Record<string, never> | undefined,
    ),
  );
}
